import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CheckCircle2,
  Circle,
  Eye,
  ShieldCheck,
  Lightbulb,
  RefreshCw,
  Users,
  FileCode,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const bestPractices = [
  {
    icon: Eye,
    title: 'Always Review Generated Code',
    description: 'Studies show 78% of AI-generated code contains exploitable vulnerabilities. Never blindly accept output.',
    tip: 'Treat AI as a junior developer—review every line before committing.',
    source: 'BlueRadius Security Review 2025',
  },
  {
    icon: Lightbulb,
    title: 'Provide Clear Context',
    description: 'Structured prompts with requirements, constraints, and examples significantly improve output quality.',
    tip: 'Focus on one function or module at a time to avoid confusing the AI.',
    source: 'Anthropic Best Practices',
  },
  {
    icon: ShieldCheck,
    title: 'Verify Security Patterns',
    description: 'AI has low success rate identifying security vulnerabilities. Use dedicated security tools alongside.',
    tip: 'Enable secret scanning, CodeQL, and dependency checks on AI-generated code.',
    source: 'arXiv Security Study 2025',
  },
  {
    icon: RefreshCw,
    title: 'Iterate Incrementally',
    description: 'Ask AI to research and plan first, then code. Complex problems need deeper thinking upfront.',
    tip: 'Start simple, then refine through back-and-forth conversation.',
    source: 'Claude Code Best Practices',
  },
  {
    icon: Users,
    title: 'Maintain Team Standards',
    description: 'Configure .copilot-instructions.md files to enforce coding standards and security checklists.',
    tip: 'Organize rules by topic: security, language-specific, and general team standards.',
    source: 'GitHub Docs 2025',
  },
  {
    icon: FileCode,
    title: 'Test Generated Code',
    description: 'AI-generated code should undergo same testing rigor as human-written code. AI achieves 70-90% accuracy.',
    tip: 'Use TDD approach: specify tests first, then have AI implement the solution.',
    source: 'ACM AI-Powered Software 2024',
  },
]

export default function BestPracticesSlide({ isActive: _isActive }: SlideProps) {
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set())
  const [hoveredItem, setHoveredItem] = useState<number | null>(null)

  const toggleItem = (index: number) => {
    const newChecked = new Set(checkedItems)
    if (newChecked.has(index)) {
      newChecked.delete(index)
    } else {
      newChecked.add(index)
    }
    setCheckedItems(newChecked)
  }

  const progress = (checkedItems.size / bestPractices.length) * 100

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
                Best <span className="text-gradient">Practices</span>
              </h2>
              <p className="text-base text-white/60">
                Essential guidelines for effective AI-assisted development
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-white/60">
                {checkedItems.size}/{bestPractices.length} completed
              </div>
              <div className="w-24 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-electric-cyan to-electric-purple"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </div>
        </StaggerItem>

        {/* Checklist */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 h-full">
            {bestPractices.map((practice, index) => {
              const Icon = practice.icon
              const isChecked = checkedItems.has(index)
              const isHovered = hoveredItem === index

              return (
                <motion.div
                  key={practice.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.08 }}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={() => toggleItem(index)}
                  className={`glass rounded-xl p-4 cursor-pointer transition-all duration-300 flex flex-col ${
                    isChecked ? 'bg-electric-cyan/10 ring-1 ring-electric-cyan/30' : ''
                  } ${isHovered ? 'glow-cyan' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <motion.div
                      whileTap={{ scale: 0.9 }}
                      className="flex-shrink-0 mt-1"
                    >
                      {isChecked ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                        >
                          <CheckCircle2 className="w-7 h-7 text-electric-cyan" />
                        </motion.div>
                      ) : (
                        <Circle className="w-7 h-7 text-white/30" />
                      )}
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
                            isChecked
                              ? 'bg-electric-cyan/20'
                              : 'bg-white/10'
                          }`}
                        >
                          <Icon
                            className={`w-5 h-5 ${
                              isChecked ? 'text-electric-cyan' : 'text-white/60'
                            }`}
                          />
                        </div>
                        <h3
                          className={`text-lg font-semibold transition-colors leading-tight ${
                            isChecked ? 'text-electric-cyan' : 'text-white'
                          }`}
                        >
                          {practice.title}
                        </h3>
                      </div>
                      <p
                        className={`text-base leading-relaxed transition-colors ${
                          isChecked ? 'text-white/70' : 'text-white/60'
                        }`}
                      >
                        {practice.description}
                      </p>
                      <div className="flex items-start gap-2 mt-2 p-2 rounded bg-white/5">
                        <Lightbulb className="w-4 h-4 text-electric-purple flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-white/50 leading-snug">
                          {practice.tip}
                        </p>
                      </div>
                      <p className="text-xs text-white/40 mt-2">
                        {practice.source}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </StaggerItem>

        {/* Footer */}
        <StaggerItem>
          <div className="mt-3 mb-[100px] text-center">
            <p className="text-xs text-white/40">
              Sources: GitHub Docs • Anthropic Engineering • arXiv Research • ACM AI-Powered Software • BlueRadius Security
            </p>
            <AnimatePresence>
              {checkedItems.size === bestPractices.length && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="mt-2"
                >
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-electric-cyan/20 to-electric-purple/20 border border-electric-cyan/30 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-electric-cyan" />
                    <span className="text-white font-medium">All practices acknowledged!</span>
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
